import { groupBy, uniq } from 'es-toolkit';

import { Bundle, BundleWithoutId, ProductionComponentInstance, Truss } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { enqueueError } from '@paragon/notification-tools';
import { InstancePositions } from '@paragon/production-app-utilities';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import {
  bundleWidthLimit,
  getAnalysisSetGuidQuantitiesByBundle,
  getBundlesByProductionGroupGuid,
  getProductionComponentInstanceGuidsForQuantity,
  getTotalThicknessByBundleGuid,
} from '../selectors/bundles';
import { getProductionComponentInstancesByBundleGuid } from '../selectors/getProductionComponentInstancesByBundleGuid';
import { getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTabNotBundledByAnalysisSetGuid } from '../selectors/getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTabNotBundledByAnalysisSetGuid';
import {
  getAnalysisSetGuidToQuantityForUnassigned,
  getOrderedAnalysisSetGuidsForUnbundledInActiveProductionGroup,
} from '../selectors/productionGroupGrid';
import { getLoadedTrussesByAnalysisSetGuid } from '../selectors/trusses';
import { checkRows, setAutoSelectNextWhenBundling } from '../store/batchesAndBundlesTabSlice';
import { bundleCreated, bundleDeleted, bundlesLoaded } from '../store/bundlesSlice';
import { productionComponentInstancesLoaded } from '../store/productionComponentInstancesSlice';
import { removeProductionComponentInstancesFromBundle } from './productionComponentInstances';
import { AsyncThunkActionVoid, ThunkActionVoid } from './thunkActions';

export function loadBundlesForProductionGroup(productionGroupGuid: string, spinner = true): AsyncThunkActionVoid {
  return async function loadBundlesForProductionGroupThunkAction(dispatch, getState) {
    const spinnerId = spinner ? dispatch(addSpinnerMessage('Loading bundles for production group...')) : null;

    try {
      const bundlesByProductionGroupGuid = getBundlesByProductionGroupGuid(getState());
      if (bundlesByProductionGroupGuid[productionGroupGuid]?.length > 0) return;

      const bundlesForProductionGroup = await designService.bundles.getForProductionGroup(productionGroupGuid);
      dispatch(bundlesLoaded(bundlesForProductionGroup));
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError('Error loading bundles'));
    } finally {
      if (spinnerId != null) dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function loadBundlesForScheduleGroup(scheduleGroupGuid: string, spinner = true): AsyncThunkActionVoid {
  return async function loadBundlesForScheduleGroupThunkAction(dispatch) {
    const spinnerId = spinner ? dispatch(addSpinnerMessage('Loading bundles for schedule group...')) : null;

    try {
      const bundlesForScheduleGroup = await designService.bundles.getForScheduleGroup(scheduleGroupGuid);
      dispatch(bundlesLoaded(bundlesForScheduleGroup));
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError('Error loading bundles'));
    } finally {
      if (spinnerId !== null) dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function createOrAddToBundleFromQuantitiesByAnalysisSetGuid(
  analysisSetGuidToQuantity: { [analysisSetGuid: string]: number },
  bundleGuid: string | null,
): AsyncThunkActionVoid {
  return async function createOrAddToBundleFromQuantitiesByAnalysisSetGuidThunkAction(dispatch, getState) {
    const spinnerMessage = bundleGuid ? 'Adding to bundle...' : 'Creating bundle...';
    const spinnerId = dispatch(addSpinnerMessage(spinnerMessage));

    const state = getState();
    const productionGroupGuid = state.batchesAndBundlesTab.activeProductionGroupGuid;
    if (!productionGroupGuid) throw new Error('Cannot add to bundle without active production group');

    const creatingBundle = bundleGuid === null;

    try {
      let bundleGuidForProductionComponents: string;
      if (creatingBundle) {
        bundleGuidForProductionComponents = crypto.randomUUID();
        const bundleWithoutId: BundleWithoutId = {
          guid: bundleGuidForProductionComponents,
          productionGroupGuid,
          scheduleGroupGuid: null,
          scheduleLineGroupGuid: null,
          scheduleLineGroupPriority: null,
          truckloadGuid: null,
        };
        const bundleWithId = await designService.bundles.insert(bundleWithoutId);
        dispatch(bundleCreated(bundleWithId));
      } else {
        bundleGuidForProductionComponents = bundleGuid;
      }

      const instancePositions = getInitialInstancePositions(
        analysisSetGuidToQuantity,
        getLoadedTrussesByAnalysisSetGuid(state),
        getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTabNotBundledByAnalysisSetGuid(state),
        bundleGuid === null ? 0 : getTotalThicknessByBundleGuid(state)[bundleGuid],
      );
      const updatedProductionComponentInstances = await designService.productionComponentInstances.patchAppendToBundle(
        instancePositions,
        bundleGuidForProductionComponents,
      );
      dispatch(productionComponentInstancesLoaded(updatedProductionComponentInstances));

      if (creatingBundle && getState().batchesAndBundlesTab.autoSelectNextWhenBundling)
        dispatch(selectInstancesForNextBundle());
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError('Error creating bundle'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

/**
 * Initially stack all the components vertically
 */
function getInitialInstancePositions(
  quantities: { [analysisSetGuid: string]: number },
  loadedTrussesByAnalysisSetGuid: { [analysisSetGuid: string]: Truss },
  productionComponentInstancesForActiveProductionGroupNotBundledByAnalysisSetGuid: {
    [analysisSetGuid: string]: readonly ProductionComponentInstance[];
  },
  initialY: number,
): InstancePositions {
  const instancePositions: InstancePositions = {};
  let currentY = initialY;
  for (const [analysisSetGuid, quantity] of Object.entries(quantities)) {
    const truss = loadedTrussesByAnalysisSetGuid[analysisSetGuid];
    const thickness = truss.numberOfPlies * truss.thickness;
    const instanceGuids = getProductionComponentInstanceGuidsForQuantity(
      productionComponentInstancesForActiveProductionGroupNotBundledByAnalysisSetGuid,
      analysisSetGuid,
      quantity,
    );
    for (const instanceGuid of instanceGuids) {
      instancePositions[instanceGuid] = {
        x: 0,
        y: currentY,
      };
      currentY += thickness;
    }
  }
  return instancePositions;
}

export function setAutoSelectInstancesForNextBundle(autoSelectNextWhenBundling: boolean): ThunkActionVoid {
  return function setAutoSelectInstancesForNextBundleThunkAction(dispatch, getState) {
    if (getState().batchesAndBundlesTab.autoSelectNextWhenBundling === autoSelectNextWhenBundling) return;
    if (autoSelectNextWhenBundling) dispatch(selectInstancesForNextBundle());
    dispatch(setAutoSelectNextWhenBundling(autoSelectNextWhenBundling));
  };
}

export function selectInstancesForNextBundle(): ThunkActionVoid {
  return function selectInstancesForNextBundleThunkAction(dispatch, getState) {
    // For quicker bundle creation, select the next components that would make a 4-foot thick bundle, staying within
    // the same batch. This may lead to selecting a quantity that is greater than will fit in a bundle, but the dialog
    // will take care of setting a partial quantity for that truss such that the max bundle size is observed.
    const orderedAnalysisSetGuidsForUnbundledInActiveProductionGroup =
      getOrderedAnalysisSetGuidsForUnbundledInActiveProductionGroup(getState());
    if (orderedAnalysisSetGuidsForUnbundledInActiveProductionGroup.length === 0) return;
    const nextAnalysisSetGuid = orderedAnalysisSetGuidsForUnbundledInActiveProductionGroup[0];
    const productionComponentInstancesForActiveProductionGroupNotBundledByAnalysisSetGuid =
      getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTabNotBundledByAnalysisSetGuid(
        getState(),
      );
    const batchGuidsForNextAnalysisSetGuid = uniq(
      productionComponentInstancesForActiveProductionGroupNotBundledByAnalysisSetGuid[nextAnalysisSetGuid].map(
        (productionComponentInstance) => productionComponentInstance.batchGuid,
      ),
    );
    if (batchGuidsForNextAnalysisSetGuid.length === 1) {
      const analysisSetGuidToQuantityForUnassigned = getAnalysisSetGuidToQuantityForUnassigned(getState());
      const loadedTrussesByAnalysisSetGuid = getLoadedTrussesByAnalysisSetGuid(getState());
      const analysisSetGuidsForProposedNextBundle: string[] = [];
      let cumulativeWidth = 0;
      let index = 0;
      let analysisSetGuid = orderedAnalysisSetGuidsForUnbundledInActiveProductionGroup[index];
      let isWithinSameBatch = uniq(
        productionComponentInstancesForActiveProductionGroupNotBundledByAnalysisSetGuid[analysisSetGuid].map(
          (productionComponentInstance) => productionComponentInstance.batchGuid,
        ),
      ).includes(batchGuidsForNextAnalysisSetGuid[0]);
      while (
        cumulativeWidth < bundleWidthLimit &&
        index < orderedAnalysisSetGuidsForUnbundledInActiveProductionGroup.length &&
        isWithinSameBatch
      ) {
        // If adding 1 of the next truss would put us over the bundle limit, stop
        const truss = loadedTrussesByAnalysisSetGuid[analysisSetGuid];
        const trussThickness = truss.thickness * truss.numberOfPlies;
        if (cumulativeWidth + trussThickness > bundleWidthLimit) break;

        // Add next component
        analysisSetGuidsForProposedNextBundle.push(analysisSetGuid);

        // Increment index
        index += 1;
        if (index === orderedAnalysisSetGuidsForUnbundledInActiveProductionGroup.length) break;

        // Update variables for next iteration
        cumulativeWidth += trussThickness * analysisSetGuidToQuantityForUnassigned[analysisSetGuid];
        analysisSetGuid = orderedAnalysisSetGuidsForUnbundledInActiveProductionGroup[index];
        isWithinSameBatch = uniq(
          productionComponentInstancesForActiveProductionGroupNotBundledByAnalysisSetGuid[analysisSetGuid].map(
            (productionComponentInstance) => productionComponentInstance.batchGuid,
          ),
        ).includes(batchGuidsForNextAnalysisSetGuid[0]);
      }
      if (analysisSetGuidsForProposedNextBundle.length > 0) dispatch(checkRows(analysisSetGuidsForProposedNextBundle));
    }
  };
}

export function duplicateBundle(bundle: Bundle): AsyncThunkActionVoid {
  return async function duplicateBundleThunkAction(dispatch, getState) {
    const state = getState();
    const analysisSetGuidQuantitiesForBundle = getAnalysisSetGuidQuantitiesByBundle(state)[bundle.guid];
    const analysisSetGuidToQuantityForUnassigned = getAnalysisSetGuidToQuantityForUnassigned(state);
    if (
      Object.entries(analysisSetGuidQuantitiesForBundle).some(
        ([analysisSetGuid, quantity]) => (analysisSetGuidToQuantityForUnassigned[analysisSetGuid] ?? 0) < quantity,
      )
    ) {
      throw new Error('Attempting to duplicate a bundle but there are not enough unbundled components to do so.');
    }

    const spinnerId = dispatch(addSpinnerMessage('Duplicating bundle...'));

    const newBundleGuid = crypto.randomUUID();
    try {
      const bundleWithoutId: BundleWithoutId = {
        guid: newBundleGuid,
        productionGroupGuid: bundle.productionGroupGuid,
        scheduleGroupGuid: null,
        scheduleLineGroupGuid: null,
        scheduleLineGroupPriority: null,
        truckloadGuid: null,
      };
      const newBundle = await designService.bundles.insert(bundleWithoutId);
      dispatch(bundleCreated(newBundle));

      const instancePositions: InstancePositions = {};
      const productionComponentInstancesForActiveProductionGroupNotBundledByAnalysisSetGuid =
        getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTabNotBundledByAnalysisSetGuid(state);
      const productionComponentInstancesForBundleGuidByAnalysisSetGuid = groupBy(
        getProductionComponentInstancesByBundleGuid(state)[bundle.guid],
        (instance) => instance.analysisSetGuid,
      );
      for (const [analysisSetGuid, quantity] of Object.entries(analysisSetGuidQuantitiesForBundle)) {
        const instanceGuidsForAnalysisSetGuid = getProductionComponentInstanceGuidsForQuantity(
          productionComponentInstancesForActiveProductionGroupNotBundledByAnalysisSetGuid,
          analysisSetGuid,
          quantity,
        );
        const instancesInBundleBeingDuplicatedForAnalysisSetGuid =
          productionComponentInstancesForBundleGuidByAnalysisSetGuid[analysisSetGuid];
        for (let i = 0; i < instanceGuidsForAnalysisSetGuid.length; i++) {
          const instanceGuidForNewBundle = instanceGuidsForAnalysisSetGuid[i];
          const instance = instancesInBundleBeingDuplicatedForAnalysisSetGuid[i];
          instancePositions[instanceGuidForNewBundle] = { x: instance.bundlePositionX!, y: instance.bundlePositionY! };
        }
      }
      const updatedProductionComponentInstances = await designService.productionComponentInstances.patchAppendToBundle(
        instancePositions,
        newBundleGuid,
      );
      dispatch(productionComponentInstancesLoaded(updatedProductionComponentInstances));
    } catch (exception) {
      dispatch(enqueueError('Error duplicating bundle'));
      console.error(exception);
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function deleteBundle(bundle: Bundle): AsyncThunkActionVoid {
  return async function deleteBundleThunkAction(dispatch, getState) {
    const spinnerId = dispatch(addSpinnerMessage('Deleting bundle...'));

    try {
      // For a cleaner user experience, optimistically visibly delete the bundle before making the multiple requests to
      // do so. If something goes wrong, the user will need to refresh, but in most cases this is preferable so that the
      // user does not need to observe the intermediate state of the components being removed from the bundle before the
      // bundle itself is deleted.
      const productionComponentInstancesByBundleGuid = getProductionComponentInstancesByBundleGuid(getState());
      dispatch(bundleDeleted(bundle));

      // Disassociate the production component instances from the bundle before deleting it
      const productionComponentInstanceGuidsForBundle = productionComponentInstancesByBundleGuid[bundle.guid].map(
        (productionComponentInstance) => productionComponentInstance.guid,
      );
      await dispatch(removeProductionComponentInstancesFromBundle(productionComponentInstanceGuidsForBundle, false));

      await designService.bundles.remove(bundle.guid);
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError('Error deleting bundle. Please refresh the page.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}
